import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { formatDate } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Info, RefreshCw, User, Crosshair, type LucideIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { loadVehicleTypeCompatibility, type VehicleTypeRow, type CompatibilityRow } from "@/lib/vehicleTypes";
import {
  useFleetCapacityDataForAllTypes,
  fetchAllUnassignedForwardJobs,
  nzHourFromIso,
  timeToHours,
  trimToHHMM,
  STS_TYPE_KEY,
} from "@/components/fleet-capacity/utils";
import {
  buildSlotSheet,
  placeUnassignedJobs,
  placeUnassignedCrossFleetJobs,
  placeScheduledBreaks,
  prepareLaneForDisplay,
  type ScheduledBreakForPlacement,
} from "@/components/fleet-capacity/slotSheetUtils";
import type { SlotBlock, FleetJobRow } from "@/components/fleet-capacity/types";
import { formatTime12 } from "@/components/roster/utils";
import { buildDayOptions } from "@/components/roster/useDriverFleetWindow";

const formatClockH = (h: number): string => {
  const hh = Math.floor(((h % 24) + 24) % 24);
  const mm = Math.round((h - Math.floor(h)) * 60);
  return formatTime12(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
};

// Matches Fleet Capacity's own "When" column -- the originally requested/
// booked time, not the real pickup time (which isn't shown here at all).
const whenLabel = (job: FleetJobRow): string => {
  const h = nzHourFromIso(job.requested_pickup_at);
  return h === null ? "—" : formatClockH(h);
};

const assignedLabel = (job: FleetJobRow): string => {
  const h = nzHourFromIso(job.assigned_at);
  return h === null ? "—" : formatClockH(h);
};

const travelLabel = (job: FleetJobRow): string => {
  if (!job.estimated_duration_seconds || job.estimated_duration_seconds <= 0) return "—";
  return `${Math.round(job.estimated_duration_seconds / 60)} min`;
};

interface DriverFleetSlotsPanelProps {
  /** Which vehicle types' lanes to show -- e.g. every icon_style="car" type,
   *  every icon_style="truck" (TMV) type, or [STS_VEHICLE_TYPE] for the STS
   *  pseudo-type. */
  vehicleTypes: VehicleTypeRow[];
  title: string;
  icon: LucideIcon;
  iconColorClass: string;
  accentColorClass: string;
  emptyMessage: string;
}

/**
 * Deliberately redacted: no pickup/dropoff address, no Stage, no Job_ID --
 * this is only meant to answer "is a vehicle free around this time", not let
 * a driver see the job well enough to go picking which ones they want.
 * Reuses the exact same slot-sheet engine (buildSlotSheet/placeScheduledBreaks)
 * Fleet Capacity uses, so open slots, real bookings, and breaks all show up
 * with the same tick granularity and time-ordering dispatchers see -- just
 * with most columns stripped out of the render. Generalised from the
 * original TMV-only panel to also serve Car/SUV and STS.
 */
const DriverFleetSlotsPanel: React.FC<DriverFleetSlotsPanelProps> = ({
  vehicleTypes,
  title,
  icon: Icon,
  iconColorClass,
  accentColorClass,
  emptyMessage,
}) => {
  const dayOptions = useMemo(buildDayOptions, []);
  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const selectedDay = dayOptions[Math.min(selectedDayIdx, dayOptions.length - 1)] || dayOptions[0];

  const { dataByType, loading, error } = useFleetCapacityDataForAllTypes(selectedDay.date, vehicleTypes);

  const [compatMatrix, setCompatMatrix] = useState<CompatibilityRow[]>([]);
  useEffect(() => {
    loadVehicleTypeCompatibility().then(setCompatMatrix);
  }, []);

  // Forward-booked jobs TaxiCaller hasn't dispatched a specific vehicle to
  // yet (vehicle_number still null) never show up via buildSlotSheet at all
  // -- it only matches jobs to a shift by vehicle_number. This is why a day
  // like tomorrow, where nothing's been dispatched yet, looked completely
  // empty without this: real bookings exist, they just don't have a vehicle
  // assigned until closer to the day. See placeUnassignedJobs.
  const [unassignedJobs, setUnassignedJobs] = useState<FleetJobRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchAllUnassignedForwardJobs().then((jobs) => {
        if (!cancelled) setUnassignedJobs(jobs);
      });
    };
    load();
    const id = setInterval(load, 25_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [selectedDay.dateStr]);

  const [publishedBreaks, setPublishedBreaks] = useState<ScheduledBreakForPlacement[]>([]);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("roster_scheduled_breaks")
      .select("driver_name, vehicle_number, suggested_start_hhmm, suggested_end_hhmm, confirmed_start_hhmm, confirmed_end_hhmm, taken")
      .eq("break_date", selectedDay.dateStr)
      .eq("status", "published")
      .then(({ data }) => {
        if (cancelled || !data) return;
        const mapped: ScheduledBreakForPlacement[] = data
          .filter((row) => row.vehicle_number)
          .map((row) => {
            const startHHMM = row.confirmed_start_hhmm || row.suggested_start_hhmm;
            const endHHMM = row.confirmed_end_hhmm || row.suggested_end_hhmm;
            return {
              vehicleNumber: row.vehicle_number as string,
              driverName: row.driver_name,
              startH: timeToHours(trimToHHMM(startHHMM)),
              endH: timeToHours(trimToHHMM(endHHMM)),
              taken: !!row.taken,
            };
          });
        setPublishedBreaks(mapped);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDay.dateStr]);

  const baseSheets = useMemo(
    () =>
      vehicleTypes.map((t) => ({
        type: t,
        sheet: buildSlotSheet(dataByType[t.vehicle_type]?.shifts || [], dataByType[t.vehicle_type]?.jobs || [], selectedDay.dateStr, t),
      })),
    [vehicleTypes, dataByType, selectedDay.dateStr]
  );

  // STS's own unassigned bookings (no vehicle yet -- see
  // fetchCrossFleetCapacity) get their own gap-seeking pass, since they
  // don't fit the vehicle-type routing placeUnassignedJobs uses -- same
  // isStsOnly branch FleetSlotSheet (the dispatcher view) uses. This panel
  // only ever renders the STS pseudo-type today (see vehicleTypes below),
  // not TTC -- see useDriverFleetWindow.ts.
  const isStsOnly = vehicleTypes.length === 1 && vehicleTypes[0].vehicle_type === STS_TYPE_KEY;

  const withUnassignedJobs = useMemo(() => {
    const afterGeneric = placeUnassignedJobs(baseSheets, unassignedJobs, compatMatrix, selectedDay.dateStr);
    if (!isStsOnly) return afterGeneric;
    const unassignedCrossFleetJobs = dataByType[STS_TYPE_KEY]?.unassignedCrossFleetJobs || [];
    return afterGeneric.map((s) => placeUnassignedCrossFleetJobs(s, unassignedCrossFleetJobs, selectedDay.dateStr));
  }, [baseSheets, unassignedJobs, compatMatrix, selectedDay.dateStr, isStsOnly, dataByType]);

  const finalSheets = useMemo(() => placeScheduledBreaks(withUnassignedJobs, publishedBreaks), [withUnassignedJobs, publishedBreaks]);

  const maxLaneCount = Math.max(0, ...finalSheets.map((s) => s.sheet.laneCount));

  interface FlatRow {
    slotLabel: string;
    block: SlotBlock;
    slotBadge: string;
  }

  const rows: FlatRow[] = useMemo(() => {
    const grouped: { label: string; blocks: SlotBlock[] }[] = [];
    for (let slotIdx = 0; slotIdx < maxLaneCount; slotIdx++) {
      for (const { type, sheet } of finalSheets) {
        if (slotIdx < sheet.lanes.length) {
          const isOverflow = sheet.hasOverflow && slotIdx === sheet.lanes.length - 1;
          grouped.push({
            label: isOverflow ? `${type.vehicle_type} Overflow` : `${type.vehicle_type} ${slotIdx + 1}`,
            blocks: prepareLaneForDisplay(sheet.lanes[slotIdx], type.slot_length_minutes),
          });
        }
      }
    }
    const flat: FlatRow[] = [];
    grouped.forEach((g) =>
      g.blocks.forEach((block) =>
        flat.push({
          slotLabel: g.label,
          block,
          slotBadge: block.slotTotal && block.slotTotal > 1 ? `${block.slotIndex}/${block.slotTotal}` : "1",
        })
      )
    );
    return flat.sort((a, b) => a.block.startH - b.block.startH);
  }, [finalSheets, maxLaneCount]);

  // "Now" tracking, same approach as the dispatcher's FleetSlotSheet --
  // polled every 30s rather than computed once, so the highlighted row (and
  // the "Today" tab's relevance) keeps advancing without a page refresh.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTick]);
  const isTodayView = selectedDay.dateStr === todayStr;
  const nowH = useMemo(() => {
    const now = new Date();
    return now.getHours() + now.getMinutes() / 60;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTick]);

  const nowRowIndices = useMemo(() => {
    if (!isTodayView) return new Set<number>();
    const set = new Set<number>();
    rows.forEach((r, i) => {
      if (r.block.startH <= nowH && r.block.endH > nowH) set.add(i);
    });
    return set;
  }, [rows, isTodayView, nowH]);
  const firstNowIdx = nowRowIndices.size ? Math.min(...nowRowIndices) : -1;

  const nowRowRef = useRef<HTMLTableRowElement | null>(null);
  const hasAutoScrolledRef = useRef(false);
  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [selectedDay.dateStr]);
  useEffect(() => {
    if (!hasAutoScrolledRef.current && nowRowRef.current) {
      nowRowRef.current.scrollIntoView({ block: "center" });
      hasAutoScrolledRef.current = true;
    }
  });
  const jumpToNow = () => {
    nowRowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  return (
    <Card className="bg-gray-800/40 border-gray-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <Icon className={`h-4 w-4 ${iconColorClass}`} />
          {title}
        </CardTitle>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {dayOptions.map((d, i) => (
            <button
              key={d.dateStr}
              onClick={() => setSelectedDayIdx(i)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                i === selectedDayIdx ? `${accentColorClass} text-white` : "bg-gray-800 border-gray-700 text-gray-400 hover:text-white"
              }`}
            >
              {d.label}
            </button>
          ))}
          {isTodayView && firstNowIdx !== -1 && (
            <button
              onClick={jumpToNow}
              className="ml-1 flex items-center gap-1 rounded-full border border-red-500 bg-gray-900/95 px-2.5 py-1 text-[11px] font-semibold text-red-400 shadow hover:bg-red-500 hover:text-white"
            >
              <Crosshair className="h-3 w-3" /> Jump to Now
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">{format(selectedDay.date, "EEEE")}, {formatDate(selectedDay.date)}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-1.5 bg-blue-950/30 border border-blue-800/30 rounded-md px-2.5 py-1.5">
          <Info className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-300">
            So you can tell a customer whether a vehicle is free for a return booking. Job details are kept off this
            screen on purpose -- this isn't for picking which jobs to do.
          </p>
        </div>

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Loading...
          </div>
        ) : error ? (
          <p className="text-sm text-red-400">{error}</p>
        ) : rows.length === 0 ? (
          <div className="text-center py-8">
            <Icon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">{emptyMessage}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-700 overflow-auto max-h-[65vh]">
            <table className="w-full text-sm border-collapse">
              <thead className="text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">Slot</th>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">Time</th>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">Status</th>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">When</th>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">Assign</th>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">Passenger</th>
                  <th className="sticky top-0 z-10 bg-gray-800 text-left px-3 py-2">Travel</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isBreak = r.block.kind === "break";
                  const isOpen = r.block.kind === "gap";
                  const job = r.block.kind === "job" || r.block.kind === "committed" ? r.block.job : null;
                  const isDelivered = job?.Stage === "Delivered";
                  const rowBg = isBreak ? "bg-amber-950/20" : isOpen ? "bg-green-950/10" : isDelivered ? "bg-gray-900/50" : "bg-gray-900/30";
                  const isNow = nowRowIndices.has(i);
                  const isBoxTop = isNow && !nowRowIndices.has(i - 1);
                  const isBoxBottom = isNow && !nowRowIndices.has(i + 1);
                  const nowEdgeCls = isNow ? `${isBoxTop ? "fleet-now-border-t" : ""} ${isBoxBottom ? "fleet-now-border-b" : ""}` : "";
                  const nowOverlay = isNow ? <span className="fleet-now-overlay" aria-hidden="true" /> : null;
                  const cellCls = `relative px-3 py-1.5 whitespace-nowrap ${nowEdgeCls}`;
                  return (
                    <tr key={i} ref={i === firstNowIdx ? nowRowRef : undefined} className={`border-t border-gray-800 ${rowBg}`}>
                      <td className={`${cellCls} text-gray-400 ${isNow ? "fleet-now-border-l" : ""}`}>
                        {nowOverlay}
                        {r.slotLabel}
                        {(job || isBreak) && <span className="text-gray-500"> ({r.slotBadge})</span>}
                      </td>
                      <td className={`${cellCls} text-white font-semibold`}>
                        {nowOverlay}
                        {formatClockH(r.block.startH)}
                      </td>
                      <td className={cellCls}>
                        {nowOverlay}
                        {isOpen && <span className="text-green-400 font-medium">Available</span>}
                        {isBreak && (
                          <span className="text-amber-400 font-medium">
                            ☕ Break -- {(r.block as Extract<SlotBlock, { kind: "break" }>).driverName}
                          </span>
                        )}
                        {job && (
                          <span className={`font-medium ${isDelivered ? "text-gray-500" : "text-blue-400"}`}>
                            {isDelivered ? "Delivered" : "Booked"}
                          </span>
                        )}
                      </td>
                      <td className={`${cellCls} text-gray-300`}>
                        {nowOverlay}
                        {job ? whenLabel(job) : "—"}
                      </td>
                      <td className={`${cellCls} text-gray-300`}>
                        {nowOverlay}
                        {job ? assignedLabel(job) : "—"}
                      </td>
                      <td className={`${cellCls} text-gray-300`}>
                        {nowOverlay}
                        {job ? (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" /> {job.Passenger_First_Name || "Unknown"}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={`${cellCls} text-gray-300`}>
                        {nowOverlay}
                        {job ? travelLabel(job) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverFleetSlotsPanel;
